import { classifyRouterError } from './classify-router-error';

/** sessionStorage key recording the last stale-client auto-reload (epoch ms). */
const RELOAD_GUARD_KEY = 'archipro:stale-client-reload-at';

/**
 * Router-error categories (from `classifyRouterError`) that mean the client is
 * out of step with the server — almost always a new build shipping while the
 * tab sat open. A fresh page load picks up the new build and recovers:
 *  - `chunk load`          a JS chunk / dynamically imported module 404s
 *  - `turbo-stream decode` a single-fetch `.data` response the stale client
 *                          can't decode (also covers a session/edge HTML
 *                          response, where reloading lands the real redirect)
 *  - `routeId not found`   the single-fetch result is missing the requested
 *                          route, i.e. the client/server route trees differ
 */
const STALE_CLIENT_CATEGORIES = new Set([
    'chunk load',
    'turbo-stream decode',
    'routeId not found',
]);

/** True for the deploy-race / stale-client errors a reload recovers from. */
export const isStaleClientError = (error: Error): boolean =>
    STALE_CLIENT_CATEGORIES.has(classifyRouterError(error));

/**
 * Cooldown after an auto-reload before another is allowed. Prevents reload loops
 * when a freshly deployed build is itself broken — a second failure inside this
 * window falls through to the error page instead of reloading again.
 */
export const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Pure loop-guard decision: given the last auto-reload time, may we reload now?
 * Extracted so the guard can be unit tested without touching the DOM.
 */
export const canAutoReload = (now: number, lastReloadAt: number): boolean =>
    now - lastReloadAt >= RELOAD_COOLDOWN_MS;

/**
 * Recover from the deploy-race that strands an open tab on the error page.
 *
 * When a new build ships while a tab is open, the next navigation's chunk or
 * single-fetch `.data` request hits a server the stale client can't agree with
 * — surfacing as `chunk load`, `turbo-stream decode`, or `routeId not found`
 * (the same deploy the `🎊 ... has been released` notification announces).
 * Reloading once picks up the new build. Any other error class is left alone so
 * genuine failures still surface.
 *
 * The cooldown guard (via sessionStorage) stops an infinite reload loop when the
 * new build is itself broken: the second failure within the window is not
 * reloaded. If sessionStorage is unavailable (private mode / sandboxed iframe)
 * we cannot guard against a loop, so we decline to reload.
 *
 * @returns true if a reload was triggered — the caller should stop and skip
 *          error logging, since this is an expected, self-healed deploy race.
 */
export const tryReloadOnStaleClient = (error: Error): boolean => {
    if (typeof window === 'undefined') return false;
    if (!isStaleClientError(error)) return false;

    let lastReloadAt: number;
    try {
        lastReloadAt = Number(
            window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0,
        );
    } catch {
        return false;
    }

    const now = Date.now();
    if (!canAutoReload(now, lastReloadAt)) return false;

    try {
        window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
    } catch {
        return false;
    }

    console.warn(
        '[ErrorPage] stale client after deploy — reloading once to recover',
        error.message,
    );
    window.location.reload();
    return true;
};
